import { useEffect, useState, useCallback } from 'react'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { ArrowClockwise, CheckCircle, XCircle, EnvelopeSimple, Queue as QueueIcon, Warning } from '@phosphor-icons/react'
import { getEmailMonitor, getEmailLogs, EmailLog, EmailMonitor } from '../../services/emailMonitor'
import { formatDateTimeBR } from '../../lib/date'

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'sent', label: 'Enviados' },
  { value: 'failed', label: 'Falhas' },
]

function n(v: string | number | undefined): number {
  return typeof v === 'number' ? v : parseInt(v || '0') || 0
}

export default function EmailsPage() {
  const [monitor, setMonitor] = useState<EmailMonitor | null>(null)
  const [logs, setLogs] = useState<EmailLog[]>([])
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all')
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const load = useCallback(async () => {
    setRefreshing(true)
    try {
      const [mon, logsRes] = await Promise.all([
        getEmailMonitor(),
        getEmailLogs({ limit: 100, status }),
      ])
      setMonitor(mon)
      setLogs(logsRes.logs)
      setTotal(logsRes.total)
    } catch (e) {
      console.error('Erro ao carregar monitor de emails', e)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [status])

  useEffect(() => {
    load()
    const t = setInterval(load, 15000) // auto-refresh
    return () => clearInterval(t)
  }, [load])

  const stat = (label: string, value: number, tone: 'green' | 'red' | 'gray') => (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <p className={`text-3xl font-bold ${tone === 'green' ? 'text-emerald-600' : tone === 'red' ? 'text-red-600' : 'text-gray-900'}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  )

  const queueBox = (title: string, q: any) => (
    <div className="flex-1 rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <QueueIcon size={18} className="text-gray-500" />
        <h3 className="font-medium text-gray-900">{title}</h3>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center text-sm">
        {[
          ['Na fila', n(q?.waiting), 'text-gray-700'],
          ['Ativo', n(q?.active), 'text-blue-600'],
          ['Falhas', n(q?.failed), n(q?.failed) > 0 ? 'text-red-600' : 'text-gray-700'],
          ['Recentes', n(q?.completed), 'text-emerald-600'],
        ].map(([l, v, c]) => (
          <div key={l as string}>
            <p className={`text-xl font-semibold ${c}`}>{v as number}</p>
            <p className="text-xs text-gray-500">{l as string}</p>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <AppLayout>
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={24} className="text-emerald-600" />
              <h1 className="text-2xl font-semibold text-gray-900">Monitor de Emails</h1>
            </div>
            <Button variant="outline" onClick={load} disabled={refreshing} className="gap-2">
              <ArrowClockwise size={18} className={refreshing ? 'animate-spin' : ''} />
              Atualizar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="text-center text-gray-500 py-16">Carregando...</div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
              {stat('Enviados (24h)', n(monitor?.stats.sent24h), 'green')}
              {stat('Falhas (24h)', n(monitor?.stats.failed24h), n(monitor?.stats.failed24h) > 0 ? 'red' : 'gray')}
              {stat('Enviados (7d)', n(monitor?.stats.sent7d), 'gray')}
              {stat('Falhas (7d)', n(monitor?.stats.failed7d), n(monitor?.stats.failed7d) > 0 ? 'red' : 'gray')}
            </div>
            {/* Delivery (SES events) */}
            <div className="grid grid-cols-3 gap-6 mb-6">
              {stat('Entregues (7d)', n(monitor?.stats.delivered7d), 'green')}
              {stat('Bounces (7d)', n(monitor?.stats.bounced7d), n(monitor?.stats.bounced7d) > 0 ? 'red' : 'gray')}
              {stat('Spam (7d)', n(monitor?.stats.complaint7d), n(monitor?.stats.complaint7d) > 0 ? 'red' : 'gray')}
            </div>

            {/* Queue health */}
            <div className="mb-1 text-xs text-gray-500">
              Filas (estado atual em tempo real — não é o histórico). "Na fila" = aguardando envio · "Falhas" = travadas após tentativas · "OK" = processadas recentes (mantém as últimas 100).
            </div>
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              {queueBox('Fila de Emails', monitor?.queues.email)}
              {queueBox('Fila de Notificações', monitor?.queues.notification)}
            </div>

            <div className="mb-2 text-sm font-medium text-gray-700">Histórico de emails ({total})</div>

            {/* Filters */}
            <div className="flex items-center gap-2 mb-4">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setStatus(f.value)}
                  className={`px-3 py-1.5 text-sm rounded-lg border ${
                    status === f.value
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <span className="ml-auto text-sm text-gray-500">{total} registros</span>
            </div>

            {/* Logs */}
            <Card>
              <CardContent className="p-0">
                {logs.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    <EnvelopeSimple size={40} className="mx-auto text-gray-300 mb-3" />
                    Nenhum email registrado ainda.
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {logs.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 px-4 py-3">
                        {log.status === 'sent' ? (
                          <CheckCircle size={20} className="text-emerald-500 mt-0.5 shrink-0" weight="fill" />
                        ) : (
                          <XCircle size={20} className="text-red-500 mt-0.5 shrink-0" weight="fill" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900 text-sm">{log.recipient}</span>
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">{log.template || '—'}</span>
                            {log.deliveryStatus === 'delivered' && (
                              <span className="px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded">Entregue</span>
                            )}
                            {log.deliveryStatus === 'bounced' && (
                              <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">Retornou (bounce)</span>
                            )}
                            {log.deliveryStatus === 'complaint' && (
                              <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">Spam</span>
                            )}
                            {log.status === 'sent' && !log.deliveryStatus && (
                              <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">Aguardando confirmação</span>
                            )}
                          </div>
                          {log.error && (
                            <p className="text-xs text-red-600 mt-1 flex items-start gap-1">
                              <Warning size={13} className="mt-0.5 shrink-0" />
                              <span className="break-all">{log.error}</span>
                            </p>
                          )}
                          {log.deliveryDetail && log.deliveryStatus !== 'delivered' && (
                            <p className="text-xs text-gray-500 mt-1 break-all">{log.deliveryDetail}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">{formatDateTimeBR(log.createdAt)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </AppLayout>
  )
}
